import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { AppDispatch, RootState } from "../../redux/store";
import {
  CartPaymentMethod as PaymentMethod,
  checkoutCart,
  clearCart,
  selectUserCartSummary,
  selectUserCartSummaryAtm,
  selectUserCartItems,
  toCartCheckoutItem,
} from "../../features/store/cartSlice";
import {
  resetPurchaseState as resetTransferState,
  setPaymentDetails as setTransferPaymentDetails,
  selectTransferPayment,
} from "../../features/store/transferPaymentSlice";
import { convertCurrencyAmount, formatCurrency } from "../../utils/utils";
import { ForwardIcon, Visa, MasterCard } from "../../assets/DirectTopUpIcon";
import { useAppSelector } from "../../hooks/hooks";
import BankTransferDetails from "./BankTransferDetails";
import { isSwitchedOff, OPAY_CHECKOUT_SETTING } from "../../utils/settings";

interface CartPaymentModalProps {
  isOpen: boolean;
  userId: string;
  targetCurrency?: any;
  currencies?: any[];
  dgCoinCurrency?: any;
  dgPointCurrency?: any;
  user?: {
    dg_coin_wallet_balance: number;
    dg_point_wallet_balance: number;
  };
  onClose: () => void;
  clickref?: React.RefObject<HTMLDivElement>;
}

const CartPaymentModal: React.FC<CartPaymentModalProps> = ({
  isOpen,
  userId,
  targetCurrency,
  currencies = [],
  dgCoinCurrency,
  dgPointCurrency,
  user,
  onClose,
  clickref,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(
    null,
  );
  const [processingMethod, setProcessingMethod] =
    useState<PaymentMethod | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { selectedCurrency, userLocation } = useAppSelector(
    (state) => state.locale,
  );
  const transferPayment = useAppSelector(selectTransferPayment);
  const { data: appData } = useAppSelector((state) => state.appData);

  // Once the backend has taken the order its purchases exist, so the cart
  // must be emptied — checking it out again would buy everything twice. It is
  // only emptied when the modal closes, because the cart page swaps to its
  // empty state (unmounting this modal) the moment the cart is empty.
  const checkedOutRef = useRef(false);
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;
  const clearCheckedOutCart = () => {
    if (!checkedOutRef.current) return;
    checkedOutRef.current = false;
    dispatch(clearCart({ userId }));
  };

  // Start clean each time the modal is reopened.
  useEffect(() => {
    if (!isOpen) {
      clearCheckedOutCart();
      dispatch(resetTransferState());
      setSuccessMessage(null);
      setProcessingMethod(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, dispatch]);

  // Leaving the page (e.g. "View Orders") without closing the modal.
  useEffect(
    () => () => clearCheckedOutCart(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const isAtmMethod =
    selectedMethod === "ATM" ||
    selectedMethod === "TRANSFER" ||
    selectedMethod === "OPAY" ||
    selectedMethod === "BLUSALT" ||
    selectedMethod === "NOWPAY";

  // Raw cart items
  const rawItems = useSelector((state: RootState) =>
    selectUserCartItems(state, userId),
  );

  // Normal summary
  const standardSummary = useSelector((state: RootState) =>
    selectUserCartSummary(
      state,
      userId,
      targetCurrency,
      currencies,
      dgCoinCurrency,
      dgPointCurrency,
    ),
  );

  // ATM summary & mapped ATM items
  const atmSummaryData = useSelector((state: RootState) =>
    selectUserCartSummaryAtm(
      state,
      userId,
      targetCurrency,
      currencies,
      dgCoinCurrency,
      dgPointCurrency,
    ),
  );

  // Select current summary and items based on active method
  const activeSummary = isAtmMethod ? atmSummaryData : standardSummary;
  const activeItems = isAtmMethod ? atmSummaryData.items : rawItems;

  const showBankTransfer =
    selectedCurrency?.symbol === "₦" ||
    selectedCurrency?.name?.toLowerCase().includes("naira");

  // Same availability rules as the single-product PaymentModal (and the
  // backend's PaymentMethodGate): Blusalt, transfer and OPay for Nigerian
  // users only; card via ATM and crypto for everyone else.
  const detectedCountry = userLocation?.data?.countryCode?.toUpperCase();
  const isNigerianUser = detectedCountry
    ? detectedCountry === "NG"
    : showBankTransfer;
  const showBlusalt = isNigerianUser;
  const showTransfer = showBankTransfer && isNigerianUser;
  // Admin switch on the Settings page ("opay_checkout").
  const showOpay =
    showTransfer && !isSwitchedOff(appData?.settings, OPAY_CHECKOUT_SETTING);
  const showAtmCard = !isNigerianUser;

  // DG Coins top the wallet up, so they cannot be paid for from it.
  const hasDGCoins = rawItems.some((item) => item.type === "dg_coins");
  const isProcessing = processingMethod !== null;

  const currencySymbol = targetCurrency?.symbol || currencies[0]?.symbol || "₦";

  const shouldEarnBonus = selectedMethod !== "POINT";

  const coinBalance = Number(user?.dg_coin_wallet_balance ?? 0);
  const pointBalance = Number(user?.dg_point_wallet_balance ?? 0);

  const isCoinDisabled =
    !user ||
    hasDGCoins ||
    isProcessing ||
    coinBalance < activeSummary.totalCoins;
  const isPointDisabled =
    !user ||
    hasDGCoins ||
    isProcessing ||
    pointBalance < activeSummary.totalPoints;

  const getCurrencyById = (currencyId?: number) =>
    currencies.find((c: any) => c.id === currencyId) ||
    targetCurrency ||
    currencies[0];

  const handlePayment = async (method: PaymentMethod) => {
    if (isProcessing) return;
    setSelectedMethod(method);
    setProcessingMethod(method);
    if (method === "TRANSFER") {
      dispatch(resetTransferState());
    }

    try {
      const res = await dispatch(
        checkoutCart({
          userId,
          payment_method: method,
          currency_id: targetCurrency?.id || selectedCurrency?.id,
          source: "WEB",
          items: rawItems.map(toCartCheckoutItem),
        }),
      ).unwrap();
      checkedOutRef.current = true;

      // Closed while the request was in flight: nothing left to show, but
      // the cart has still been bought.
      if (!isOpenRef.current) {
        clearCheckedOutCart();
        return;
      }

      // Hosted checkout (ATM / OPay / Blusalt / crypto)
      if (res.data?.payment_url) {
        clearCheckedOutCart();
        window.location.href = res.data.payment_url;
        return;
      }

      // Bank transfer: show the account to pay into
      if (method === "TRANSFER" && res.data?.account_number) {
        dispatch(
          setTransferPaymentDetails({
            bank: res.data.bank_name,
            bank_name: res.data.bank_name,
            account_name: res.data.account_name,
            account_number: res.data.account_number,
            reference: res.data.reference,
            accountExpiry: res.data.accountExpiry,
            amount: res.data.amount,
          }),
        );
        setProcessingMethod(null);
        return;
      }

      // Coin / point wallet: paid, items are being delivered
      setSuccessMessage(res.message);
      setProcessingMethod(null);
    } catch (err: any) {
      toast.error(err?.message || "Checkout failed");
      setProcessingMethod(null);
    }
  };

  const handleTransferProcessingFallback = () => {
    dispatch(resetTransferState());
    setSuccessMessage(
      "Your transfer is processing. Your items will be delivered once it is confirmed.",
    );
  };

  const handleTransferSuccess = () => {
    dispatch(resetTransferState());
    setSuccessMessage(
      "Payment received. Your items will be delivered one after the other.",
    );
  };

  const buttonLabel = (method: PaymentMethod, label: string) =>
    processingMethod === method ? "Processing..." : label;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        ref={clickref}
        className="bg-white dark:bg-black rounded-lg p-4 sm:p-6 w-full max-w-md my-auto max-h-[90vh] overflow-y-auto"
      >
        <div className="flex justify-between items-center mb-4 sticky top-0 bg-white dark:bg-black py-1 z-20">
          <h2 className="text-sm bp:text-base font-semibold text-black dark:text-white">
            Cart Checkout ({activeSummary.totalQuantity} items)
          </h2>
          <button
            onClick={onClose}
            className="text-[#475467] dark:text-white bg-[#F4F4F5] dark:bg-[#FFFFFF1A] rounded-2xl h-7 w-7 flex items-center justify-center hover:text-gray-700 dark:hover:text-gray-200 shrink-0"
          >
            &times;
          </button>
        </div>

        {shouldEarnBonus && activeSummary.totalBonusMonetaryValue > 0 && (
          <div className="bg-[#FFF9E7] dark:bg-[#DC6803] p-3 rounded-3xl mb-6 flex items-center justify-center gap-2">
            <img
              src="/png/dg.png"
              alt="Coins"
              className="w-[24px] h-[24px] shrink-0"
            />
            <p className="text-[10px] bp:text-sm text-[#DC6803] dark:text-white">
              Earn {currencySymbol}
              {formatCurrency(activeSummary.totalBonusMonetaryValue)} in DG
              points bonus from this purchase
            </p>
          </div>
        )}

        {/* Items List */}
        <div className="flex flex-col gap-2 mb-4 max-h-40 overflow-y-auto pr-1">
          {activeItems.map((item) => {
            const fromCurrency = getCurrencyById(item.priceCurrencyId);
            const toCurrency = targetCurrency || fromCurrency;
            const unitPrice = convertCurrencyAmount(
              item.price,
              fromCurrency,
              toCurrency,
            );

            return (
              <div
                key={item.id}
                className="flex items-center gap-3 border border-border-border1 dark:border-tabs p-2 rounded-lg"
              >
                <img
                  src={item.image || "/png/dg.png"}
                  alt={item.name}
                  className="w-12 h-12 rounded-lg object-cover shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <h4 className="text-xs font-bold text-black dark:text-white truncate">
                    {item.name}
                  </h4>
                  <p className="text-[10px] text-gray-500 dark:text-gray-400">
                    Qty: {item.quantity} × {currencySymbol}
                    {formatCurrency(unitPrice)}
                  </p>
                </div>
                <span className="text-xs font-semibold text-black dark:text-white">
                  {currencySymbol}
                  {formatCurrency(unitPrice * item.quantity)}
                </span>
              </div>
            );
          })}
        </div>

        {/* Price Breakdowns */}
        <div className="border-t border-gray-200 dark:border-gray-700 py-4">
          <div className="flex justify-between mb-1 bp:mb-2 font-inter">
            <span className="text-[#667085] dark:text-white text-[10px] bp:text-sm">
              Subtotal
            </span>
            <span className="text-black text-[10px] bp:text-sm dark:text-white">
              {currencySymbol}
              {formatCurrency(activeSummary.subtotal)}
            </span>
          </div>

          {activeSummary.totalDiscount > 0 && (
            <div className="flex justify-between mb-1 bp:mb-2 font-inter">
              <span className="text-[#667085] text-[10px] bp:text-sm dark:text-white">
                Discount ({activeSummary.effectiveDiscountPercent}%)
              </span>
              <span className="text-black dark:text-white text-[10px] bp:text-sm">
                -{currencySymbol}
                {formatCurrency(activeSummary.totalDiscount)}
              </span>
            </div>
          )}
        </div>

        {/* Total */}
        <div className="border-t border-dashed border-gray-200 dark:border-white py-4 font-inter">
          <div className="flex justify-between items-center">
            <span className="text-[#101828] text-[10px] bp:text-sm dark:text-gray-400">
              Total
            </span>
            <div className="flex items-center gap-2">
              <div className="bg-[#039EF2] text-white px-2 py-1 rounded-2xl gap-1 flex items-center">
                <img
                  src="/png/dg.png"
                  alt="Coins"
                  className="w-[16px] h-[16px]"
                />
                <span className="text-[10px] bp:text-xs font-inter font-semibold whitespace-nowrap">
                  {formatCurrency(activeSummary.totalCoins)}
                </span>
              </div>
              <span className="text-[10px] bp:text-sm font-bold text-black dark:text-white whitespace-nowrap">
                {currencySymbol}
                {formatCurrency(activeSummary.grandTotal)}
              </span>
            </div>
          </div>
        </div>

        {/* Payment Methods */}
        <div className="mt-2 bp:mt-6 bg-[#EAECF0] dark:bg-[#FFFFFF1A] rounded-lg p-3 sm:p-4 flex flex-col gap-3">
          {transferPayment.paymentDetails ? (
            <BankTransferDetails
              onProcessingFallback={handleTransferProcessingFallback}
              onSuccess={handleTransferSuccess}
            />
          ) : successMessage ? (
            <div className="bg-white dark:bg-[#FFFFFF1A] rounded-lg p-4 text-center">
              <h3 className="text-sm bp:text-base font-semibold text-black dark:text-white">
                Checkout Successful
              </h3>
              <p className="mt-2 text-xs bp:text-sm text-gray-600 dark:text-gray-300">
                {successMessage}
              </p>
              <Link
                to="/order-history"
                className="inline-flex items-center justify-center mt-4 w-full border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-4 py-2 rounded-3xl text-xs bp:text-sm dark:text-white dark:bg-[#FFFFFF1A] hover:bg-gray-100 dark:hover:bg-[#FFFFFF33]"
              >
                View Orders
              </Link>
            </div>
          ) : (
            <>
              {/* Coin Wallet Option */}
              <button
                disabled={isCoinDisabled}
                className={`relative min-h-[50px] bp:min-h-[60px] w-full flex items-center justify-between rounded-lg p-3 sm:p-4 overflow-hidden transition-opacity ${
                  isCoinDisabled
                    ? "opacity-50 cursor-not-allowed"
                    : "cursor-pointer"
                }`}
                style={{
                  backgroundImage: `url(/images/bg.png)`,
                  backgroundPosition: "center bottom",
                  backgroundSize: "cover",
                }}
                onClick={() => handlePayment("COIN")}
              >
                <div className="flex items-center gap-3 z-10">
                  <img
                    src="/png/dg.png"
                    alt="Coins"
                    className="w-[32px] h-[32px] shrink-0"
                  />
                  <div className="flex flex-col text-left">
                    <p className="text-[8px] font-medium text-blue-100">
                      {processingMethod === "COIN"
                        ? "Processing..."
                        : hasDGCoins
                          ? "Not available for DG Coins"
                          : isCoinDisabled && !isProcessing
                            ? "Insufficient Coin Balance"
                            : "Pay with available coin"}
                    </p>
                    <p className="text-base sm:text-xl font-bold text-white">
                      {formatCurrency(coinBalance)}
                    </p>
                  </div>
                </div>
                <div className="bg-white w-6 h-6 flex items-center justify-center rounded-full p-1 z-10 shrink-0">
                  <ForwardIcon />
                </div>
              </button>

              {/* Point Wallet Option */}
              <button
                disabled={isPointDisabled}
                className={`relative min-h-[50px] bp:min-h-[60px] w-full flex items-center justify-between rounded-lg p-3 sm:p-4 overflow-hidden bg-[#FFF4EA] transition-opacity ${
                  isPointDisabled
                    ? "opacity-50 cursor-not-allowed"
                    : "cursor-pointer"
                }`}
                onClick={() => handlePayment("POINT")}
              >
                <div className="flex items-center gap-3 z-10">
                  <img
                    src="/images/diamond.png"
                    alt="Points"
                    className="w-[32px] h-[32px] shrink-0"
                  />
                  <div className="flex flex-col text-left">
                    <p className="text-[8px] font-medium text-[#CE8B41] whitespace-nowrap">
                      {processingMethod === "POINT"
                        ? "Processing..."
                        : hasDGCoins
                          ? "Not available for DG Coins"
                          : isPointDisabled && !isProcessing
                            ? "Insufficient Point Balance"
                            : "Pay with available point"}
                    </p>
                    <p className="text-base sm:text-xl font-bold text-[#CE8B41]">
                      {formatCurrency(pointBalance)}
                    </p>
                  </div>
                </div>
                <div className="bg-white w-6 h-6 flex items-center justify-center rounded-full p-1 z-10 shrink-0">
                  <ForwardIcon />
                </div>
              </button>

              {/* Bank Transfer Option */}
              {showTransfer && (
                <button
                  disabled={isProcessing}
                  className="w-full bg-white dark:bg-[#FFFFFF1A] border border-gray-300 dark:border-gray-700 rounded-lg p-3 flex items-center justify-center text-black dark:text-white hover:bg-gray-50 dark:hover:bg-[#ffffff26] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => handlePayment("TRANSFER")}
                >
                  <span className="text-[10px] dark:text-white bp:text-base font-semibold">
                    {buttonLabel("TRANSFER", "Pay with Bank Transfer")}
                  </span>
                </button>
              )}

              {/* OPay Option */}
              {showOpay && (
                <button
                  disabled={isProcessing}
                  className="w-full bg-white dark:bg-[#FFFFFF1A] border border-gray-300 dark:border-gray-700 rounded-lg p-3 flex items-center justify-center text-black dark:text-white hover:bg-gray-50 dark:hover:bg-[#ffffff26] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => handlePayment("OPAY")}
                >
                  <span className="w-6 h-6 mr-2 flex items-center justify-center shrink-0">
                    <svg
                      viewBox="0 0 48 48"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-6 h-6"
                    >
                      <circle
                        cx="24"
                        cy="24"
                        r="16"
                        stroke="#52BE95"
                        strokeWidth="8"
                        fill="none"
                      />
                      <rect
                        x="2"
                        y="20"
                        width="18"
                        height="8"
                        rx="2"
                        fill="#1B1B4D"
                      />
                    </svg>
                  </span>
                  <span className="text-[10px] dark:text-white bp:text-base font-semibold">
                    {buttonLabel("OPAY", "Pay with OPay")}
                  </span>
                </button>
              )}

              {/* Blusalt Option */}
              {showBlusalt && (
                <button
                  disabled={isProcessing}
                  className="w-full bg-white dark:bg-[#FFFFFF1A] border border-gray-300 dark:border-gray-700 rounded-lg p-3 flex items-center justify-center text-black dark:text-white hover:bg-gray-50 dark:hover:bg-[#ffffff26] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => handlePayment("BLUSALT")}
                >
                  <span className="w-6 h-6 mr-2 flex items-center justify-center shrink-0">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-5 h-5 text-[#0052FF]"
                    >
                      <path
                        d="M12 2L2 7L12 12L22 7L12 2Z"
                        fill="currentColor"
                      />
                      <path
                        d="M2 17L12 22L22 17"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M2 12L12 17L22 12"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  <span className="text-[10px] dark:text-white bp:text-base font-semibold">
                    {buttonLabel(
                      "BLUSALT",
                      "Pay with Card/ Momo/ Opay/ Palmpay",
                    )}
                  </span>
                </button>
              )}

              {/* Crypto Option */}
              <button
                disabled={isProcessing}
                className="w-full bg-white dark:bg-[#FFFFFF1A] border border-gray-300 dark:border-gray-700 rounded-lg p-3 flex items-center justify-center text-black dark:text-white hover:bg-gray-50 dark:hover:bg-[#ffffff26] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => handlePayment("NOWPAY")}
              >
                <span className="w-6 h-6 mr-2 flex items-center justify-center shrink-0">
                  <svg viewBox="0 0 24 24" className="w-6 h-6">
                    <path
                      d="M23.638 14.904c-1.602 6.43-8.113 10.34-14.542 8.736C2.67 22.05-1.244 15.525.362 9.105 1.962 2.67 8.475-1.243 14.9.358c6.43 1.605 10.342 8.115 8.738 14.546zm-6.35-4.613c.24-1.59-.974-2.45-2.64-3.03l.54-2.153-1.315-.33-.525 2.107c-.345-.087-.705-.167-1.064-.25l.526-2.127-1.32-.33-.54 2.165c-.285-.067-.565-.132-.84-.2l-1.815-.45-.35 1.407s.975.225.955.238c.535.136.63.486.615.766l-1.477 5.92c-.075.166-.24.406-.614.314.015.02-.96-.24-.96-.24l-.66 1.51 1.71.426.93.242-.54 2.19 1.32.327.54-2.17c.36.1.705.19 1.05.273l-.51 2.154 1.32.33.545-2.19c2.24.427 3.93.257 4.64-1.774.57-1.637-.03-2.58-1.217-3.196.854-.193 1.5-.76 1.68-1.93zm-3.01 4.22c-.404 1.64-3.157.75-4.05.53l.72-2.9c.896.23 3.757.67 3.33 2.37zm.41-4.24c-.37 1.49-2.662.735-3.405.55l.654-2.64c.744.18 3.137.52 2.75 2.084z"
                      fill="#F7931A"
                    />
                  </svg>
                </span>
                <span className="text-[10px] dark:text-white bp:text-base font-semibold">
                  {buttonLabel("NOWPAY", "Pay with Crypto")}
                </span>
              </button>

              {/* ATM Option */}
              {showAtmCard && (
                <button
                  disabled={isProcessing}
                  className="w-full bg-white dark:bg-[#FFFFFF1A] border border-gray-300 dark:border-gray-700 rounded-lg p-3 flex items-center justify-center text-black dark:text-white hover:bg-gray-50 dark:hover:bg-[#ffffff26] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => handlePayment("ATM")}
                >
                  <div className="flex space-x-2 mr-2 shrink-0">
                    <span className="w-8">
                      <Visa />
                    </span>
                    <span className="w-8">
                      <MasterCard />
                    </span>
                  </div>
                  <span className="text-[10px] dark:text-white bp:text-base font-semibold">
                    {buttonLabel("ATM", "Pay with ATM Card")}
                  </span>
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CartPaymentModal;
